import React, { useRef, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  TextField,
  IconButton,
  Chip,
  Typography,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { Icon as OpusIcon } from "../common/opus";
import { LAYERS, normalizeLayer, layerMeta } from "../../services/scenes/captionPayload";

// DataGrid steals arrow/space/home/end keystrokes for cell navigation unless
// interactive cell content stops propagation before it reaches the grid.
const stopKeyPropagation = (e) => e.stopPropagation();

// Standard-variant TextField always paints its underline, so an empty cell
// reads as a stray blank line instead of the plain blank space every other
// column (and other DataGrids in the app) shows for missing data. Hide it at
// rest and only reveal it on hover/focus, as an edit affordance.
const noChromeInputSx = {
  "& .MuiInput-root:before": { borderBottom: "none" },
  "& .MuiInput-root:hover:not(.Mui-disabled):before": { borderBottom: "1px solid var(--border)" },
  "& .MuiInput-root.Mui-focused:after": { borderBottom: "2px solid var(--seafoam-green, #2bbfa4)" },
  "& .MuiInput-root.Mui-disabled:before": { borderBottom: "none" },
};

const getVersionOptions = (caption) => {
  const versionSet = new Set();
  if (Array.isArray(caption.all_versions)) {
    caption.all_versions.forEach((item) => {
      if (typeof item === "string") versionSet.add(item);
      else if (item?.version) versionSet.add(item.version);
    });
  }
  if (Array.isArray(caption.all_captions)) {
    caption.all_captions.forEach((item) => {
      if (item?.version) versionSet.add(item.version);
    });
  }
  return Array.from(versionSet).sort();
};

// Graphic Name is disabled once a file exists (OPUS behavior), but a disabled
// MUI input dims its text to text.disabled by default — making it look
// greyed-out/inactive next to the plain, undimmed text in the File column.
// Force it back to the same undimmed color/size as that column's text.
const matchFileFontSx = {
  "& .MuiInputBase-input, & .MuiInputBase-input.Mui-disabled": {
    fontSize: "0.75rem",
    color: "var(--text)",
    WebkitTextFillColor: "var(--text)",
  },
};

const CaptionTextFieldCell = ({ value, disabled, onChange, matchFileFont, focusNonce }) => {
  const inputRef = useRef(null);

  // focusNonce changes on every add (even if the target index repeats), so
  // this fires each time a new row should grab focus — select() lets the
  // user immediately overwrite the autofilled name, or just start editing it.
  useEffect(() => {
    if (focusNonce && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [focusNonce]);

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }} onKeyDown={stopKeyPropagation}>
      <TextField
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        disabled={disabled}
        size="small"
        variant="standard"
        sx={matchFileFont ? { ...noChromeInputSx, ...matchFileFontSx } : noChromeInputSx}
        inputRef={inputRef}
      />
    </Box>
  );
};

// OPUS-1391: which of the player's stacked layers this caption renders into.
// Membership only — it never implies an order within the layer, which is why
// this is a plain per-row choice rather than any kind of grouping or ordering
// affordance. The dot repeats the layer's colour so the column can be scanned
// without reading every label.
// A bare styled <select> rather than a MUI TextField. The standard-variant
// TextField stacks an underline slot and its own dropdown icon on top of the
// native control's, which pushed the text off the row's baseline and put two
// carets in the cell. Styling the select directly keeps the dot, the label and
// the caret on one line at the same height as every other column.
const CHEVRON = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="%23808080" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>',
);

const LayerCell = ({ caption, onLayerChange }) => {
  const value = normalizeLayer(caption.layer);
  const meta = layerMeta(value);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%", height: "100%" }} onKeyDown={stopKeyPropagation}>
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
      <Box
        component="select"
        aria-label="Layer"
        value={value}
        onChange={(e) => onLayerChange(e.target.value)}
        sx={{
          appearance: "none",
          WebkitAppearance: "none",
          flex: 1,
          minWidth: 0,
          height: 24,
          m: 0,
          py: 0,
          pl: 0,
          pr: "16px",
          border: "1px solid transparent",
          borderRadius: "var(--r-sm)",
          background: "transparent",
          backgroundImage: `url("data:image/svg+xml,${CHEVRON}")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 2px center",
          color: "var(--text)",
          fontFamily: "var(--font-sans)",
          fontSize: "0.75rem",
          lineHeight: "24px",
          cursor: "pointer",
          transition: "border-color .12s, background-color .12s",
          "&:hover": { borderColor: "var(--border)", backgroundColor: "var(--surface-2)" },
          "&:focus": { outline: "none", borderColor: "var(--coral)" },
          // The dropdown itself is rendered by the OS, so it needs explicit
          // colours — it does not inherit the dark surface tokens.
          "& option": { background: "var(--elevated)", color: "var(--text)" },
        }}
      >
        {LAYERS.map((l) => (
          <option key={l.value} value={l.value}>{l.label}</option>
        ))}
      </Box>
    </Box>
  );
};

// OPUS-1391: filename only. The upload control moved to Actions so every action
// on a caption lives in one place — it used to be split between an unlabelled
// leading column (enable/disable), this cell (upload) and Actions.
const FileCell = ({ caption }) => {
  const hasUnsavedFile = Boolean(caption.file);

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%", minWidth: 0 }}>
      {hasUnsavedFile ? (
        <Chip label={caption.file.name} size="small" variant="outlined" />
      ) : caption.existingFile ? (
        <Typography variant="caption" noWrap>{`${caption.caption_name}.zip`}</Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">No file selected</Typography>
      )}
    </Box>
  );
};

const VersionCell = ({ caption, onVersionChange }) => {
  const versionOptions = getVersionOptions(caption);
  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", gap: 0.5 }} onKeyDown={stopKeyPropagation}>
      <Autocomplete
        freeSolo
        size="small"
        options={versionOptions}
        value={caption.version || ""}
        onChange={(e, newValue) => onVersionChange(newValue)}
        onInputChange={(e, newInputValue) => onVersionChange(newInputValue)}
        // OPUS-1391: the Autocomplete's endAdornment (the clear button) sits in
        // normal flow next to the input, so the input root measured 42px around
        // a 21px input. Centred in the row, that pushed the version text well
        // above every other column. Pin the adornment out of flow — which is
        // what MUI already does for the outlined and filled variants — and hold
        // the root to the same 24px line box as the rest of the row.
        sx={{
          flex: 1,
          // The Autocomplete root is display:block and measured 55px even once
          // the TextField inside it was down to 24 — so the cell centred a 55px
          // box and the text landed near its top. Constrain the root itself.
          display: "inline-flex",
          alignItems: "center",
          height: 24,
          // Measured: this input rendered 29px tall and started 2px ABOVE the
          // cell, while every other column's input was 24px starting 16px down
          // — an 18px discrepancy, which is the misalignment you can see in the
          // row. Autocomplete's own padding rules are `.MuiAutocomplete-inputRoot
          // .MuiAutocomplete-input`, so matching that depth is what lets these
          // win rather than a shallower selector silently losing.
          "& .MuiAutocomplete-inputRoot": {
            height: 24,
            minHeight: 24,
            p: 0,
            alignItems: "center",
            flexWrap: "nowrap",
          },
          "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
            p: 0,
            height: 24,
            boxSizing: "border-box",
            lineHeight: "24px",
            fontSize: "0.75rem",
          },
          "& .MuiAutocomplete-endAdornment": {
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
          },
        }}
        renderInput={(params) => <TextField {...params} variant="standard" sx={noChromeInputSx} />}
      />
      {caption.previousVersion && (
        <Tooltip title={`Adding new version: ${caption.previousVersion} → ${caption.version}`}>
          <Box component="span" sx={{ display: "flex", color: "var(--text-dim)", flexShrink: 0 }}>
            <OpusIcon name="clock" size={14} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// OPUS-1391: every per-caption action in one column, left to right in rough
// order of use: visibility, upload, edit, download, delete. Enable/disable used
// to sit in an unlabelled column at the far left of the row and upload was
// buried in the File cell, so "what can I do to this caption" was answered in
// three different places.
const ActionsCell = ({ caption, isEditing, onFileChange, onToggleDisabled, onEditCaptionDetail, onDownloadCaption, onRemoveCaption }) => {
  const inputRef = useRef(null);

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25, width: "100%", height: "100%" }} onKeyDown={stopKeyPropagation}>
      {!!onToggleDisabled && (
        <Tooltip title={caption.disabled ? "Caption disabled — click to enable" : "Disable caption (hidden from renderers)"}>
          <IconButton
            size="small"
            onClick={onToggleDisabled}
            sx={{ color: caption.disabled ? "var(--amber)" : "var(--text-dim)", "&:hover": { color: "var(--amber)" } }}
          >
            <OpusIcon name={caption.disabled ? "eyeOff" : "eye"} size={18} />
          </IconButton>
        </Tooltip>
      )}

      <input type="file" accept=".zip" hidden ref={inputRef} onChange={(e) => onFileChange(e.target.files[0])} />
      <Tooltip title="Upload caption zip">
        <IconButton size="small" color="seafoamGreen" onClick={() => inputRef.current?.click()}>
          <OpusIcon name="upload" size={18} />
        </IconButton>
      </Tooltip>

      {(isEditing || caption.existingFile) && !!onEditCaptionDetail && (
        <Tooltip title="Edit">
          <IconButton onClick={onEditCaptionDetail} color="info" size="small">
            <OpusIcon name="edit" size={18} />
          </IconButton>
        </Tooltip>
      )}
      {caption.existingFile && !!onDownloadCaption && (
        <Tooltip title="Download caption">
          <IconButton onClick={onDownloadCaption} color="info" size="small">
            <OpusIcon name="download" size={18} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Delete">
        <IconButton onClick={onRemoveCaption} color="info" size="small">
          <OpusIcon name="trash" size={18} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const CaptionsDataGrid = ({
  captions,
  isEditing,
  onCaptionChange,
  onFileChange,
  onRemoveCaption,
  onEditCaptionDetail,
  onDownloadCaption,
  onToggleDisabled,
  focusRequest,
}) => {
  // If the caller already filtered `captions`, each item carries its index in
  // the unfiltered source array as `_originalIndex` — handlers must key off
  // that, not position in this (possibly narrowed) array.
  const rows = captions.map((caption, index) => ({
    ...caption,
    _rowIndex: caption._originalIndex !== undefined ? caption._originalIndex : index,
  }));

  const columns = [
    {
      field: "caption_name",
      headerName: "Graphic Name",
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <CaptionTextFieldCell
          value={params.row.caption_name}
          disabled={isEditing && params.row.existingFile}
          onChange={(value) => onCaptionChange(params.row._rowIndex, "caption_name", value)}
          matchFileFont
          focusNonce={focusRequest && focusRequest.index === params.row._rowIndex ? focusRequest.nonce : null}
        />
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.2,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <CaptionTextFieldCell
          value={params.row.description}
          onChange={(value) => onCaptionChange(params.row._rowIndex, "description", value)}
        />
      ),
    },
    {
      field: "file",
      headerName: "File",
      flex: 1.3,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <FileCell caption={params.row} />
      ),
    },
    {
      field: "layer",
      headerName: "Layer",
      width: 130,
      // Sortable, unlike its neighbours: sorting by layer is the closest this
      // grid gets to grouping, and it is the one caption field where seeing all
      // of one kind together is genuinely useful.
      sortable: true,
      disableColumnMenu: true,
      valueGetter: (value, row) => normalizeLayer(row.layer),
      renderCell: (params) => (
        <LayerCell
          caption={params.row}
          onLayerChange={(value) => onCaptionChange(params.row._rowIndex, "layer", value)}
        />
      ),
    },
    {
      field: "version",
      headerName: "Version",
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <VersionCell
          caption={params.row}
          onVersionChange={(value) => onCaptionChange(params.row._rowIndex, "version", value)}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 210,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <ActionsCell
          caption={params.row}
          isEditing={isEditing}
          onFileChange={(file) => onFileChange(params.row._rowIndex, file)}
          onToggleDisabled={onToggleDisabled ? () => onToggleDisabled(params.row._rowIndex) : null}
          onEditCaptionDetail={onEditCaptionDetail ? () => onEditCaptionDetail(params.row, params.row._rowIndex) : null}
          onDownloadCaption={onDownloadCaption ? () => onDownloadCaption(params.row, params.row._rowIndex) : null}
          onRemoveCaption={() => onRemoveCaption(params.row._rowIndex)}
        />
      ),
    },
  ];

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={(row) => row._rowIndex}
      autoHeight
      hideFooter
      disableColumnMenu
      disableRowSelectionOnClick
      columnHeaderHeight={40}
      rowHeight={56}
      sx={{
        border: "none",
        "& .MuiDataGrid-cell": { alignItems: "center" },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
        "& .MuiDataGrid-footerContainer": { borderTop: "none" },
        // OPUS-1391: put every inline editor on the same 24px line box. Each
        // standard-variant input reserved slightly different vertical space of
        // its own — enough that Name, Description, Version and Layer each sat on
        // a different baseline and the row read as ragged.
        "& .MuiInputBase-root": { marginTop: 0 },
        "& .MuiInputBase-input": {
          height: 24,
          lineHeight: "24px",
          paddingTop: 0,
          paddingBottom: 0,
          fontSize: "0.75rem",
        },
        "& .MuiAutocomplete-inputRoot": {
          paddingTop: 0,
          paddingBottom: 0,
          flexWrap: "nowrap",
          height: 24,
          alignItems: "center",
        },
      }}
    />
  );
};

export default CaptionsDataGrid;
